import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { useGetMyWriter } from "../../../../../api/queries";
import useAppStore from "../../../../../store/appStateZustand";

// Zod schema for writer form validation
export const writerFormSchema = z.object({
  firstname: z
    .string()
    .min(1, "First name is required")
    .refine((name) => {
      // Using the existing nameRegexTest function logic
      return /^[a-z\s'-]+$/i.test(name);
    }, "Invalid characters in first name"),

  lastname: z
    .string()
    .min(1, "Last name is required")
    .refine((name) => {
      return /^[a-z\s'-]+$/i.test(name);
    }, "Invalid characters in last name"),

  middlename: z
    .string()
    .optional()
    .refine((name) => {
      if (name === undefined || name === null || name.trim() === "")
        return true;
      return /^[a-z\s'-]+$/i.test(name);
    }, "Invalid characters in middle name"),

  caeipinumber: z
    .string()
    .optional()
    .refine((cae) => {
      if (cae === undefined || cae === null || cae.trim() === "")
        return true;
      return cae.trim().length >= 9;
    }, "CAE/IPI number must be at least 9 digits"),

  societycisaccode: z.string().optional(),

  dateofbirth: z.string().optional(),

  associatedPublisherName: z
    .string()
    .optional()
    .refine((name) => {
      if (name === undefined || name === null || name.trim() === "")
        return true;
      // Publisher regex pattern - allowing alphanumeric, spaces, and common punctuation
      return /^[a-z0-9\s&.,'-]+$/i.test(name);
    }, "Invalid characters in publisher name"),

  associatedPublisherCAEIPI: z.string().optional(),
})
  .refine((data) => {
    // If either CAE/IPI or PRO is provided, both must be provided
    const hasCAEIPI = Boolean(data.caeipinumber !== undefined && data.caeipinumber !== null && data.caeipinumber.trim().length > 0);
    const hasPRO = Boolean(data.societycisaccode !== undefined && data.societycisaccode !== null && data.societycisaccode.trim().length > 0);

    if (hasCAEIPI && !hasPRO) {
      return false;
    }
    if (hasPRO && !hasCAEIPI) {
      return false;
    }
    return true;
  }, {
    message: "CAE/IPI number and PRO must both be provided or both be empty",
    path: ["caeipinumber"], // This will show the error on the CAE/IPI field
  });

export type WriterFormData = z.infer<typeof writerFormSchema>;

// Type definitions for writer data structures
type Writer = {
  writercode?: string;
  writername?: string;
  firstname?: string;
  lastname?: string;
  middlename?: string;
  caeipinumber?: string;
  societycisaccode?: string;
  dateofbirth?: string;
  publishername?: string | unknown;
  publishercaeipi?: string | unknown;
  etag?: string;
};

type UseWriterFormOptions = {
  editingWriterCode?: string | null;
  availableWriters?: Writer[];
  selectedWriters?: Writer[];
};

export function useWriterForm(options: UseWriterFormOptions = {}) {
  const { editingWriterCode, availableWriters = [], selectedWriters = [] } = options;
  const selectedAccount = useAppStore(s => s.selectedAccount);

  // Check if we're in edit mode
  const isEditing = Boolean(editingWriterCode);

  // Find the writer being edited - first check availableWriters (which has full data + etag)
  // then fall back to selectedWriters (which has limited data)
  const editingWriterFromAvailable = isEditing
    ? availableWriters.find(w => w.writercode === editingWriterCode)
    : null;
  const editingWriterFromSelected = isEditing
    ? selectedWriters.find(w => w.writercode === editingWriterCode)
    : null;

  // Only fetch from API if we don't have full data from availableWriters
  const writerQuery = useGetMyWriter({
    aac: selectedAccount as string,
    wc: editingWriterCode ?? "",
  });

  // Use writer data - prioritize availableWriters (full data), then API, then selectedWriters (limited data)
  const editingWriter = editingWriterFromAvailable ?? writerQuery.data ?? editingWriterFromSelected;

  // US PRO CISAC codes - ASCAP, BMI, SESAC, AllTrack respectively
  const usPros = useMemo(() => ["010", "021", "071", "999"], []);

  // Setup react-hook-form with Zod validation
  const form = useForm<WriterFormData>({
    resolver: zodResolver(writerFormSchema),
    defaultValues: useMemo(() => {
      if (isEditing && editingWriter != null) {
        // Use the data we have immediately (from selectedWriters or API)
        // Parse the writer name into first, middle, last if we don't have individual fields
        let firstname = "";
        let lastname = "";
        let middlename = "";

        if ("firstname" in editingWriter && editingWriter.firstname != null) {
          // We have the individual fields (from API data)
          firstname = editingWriter.firstname;
          lastname = editingWriter.lastname ?? "";
          middlename = editingWriter.middlename ?? "";
        }
        else if (editingWriter.writername != null) {
          // We only have the combined name (from selectedWriters), parse it
          const nameParts = editingWriter.writername.split(" ");
          firstname = nameParts[0] ?? "";
          lastname = nameParts.length > 1 ? (nameParts[nameParts.length - 1] ?? "") : "";
          middlename = nameParts.length > 2 ? nameParts.slice(1, -1).join(" ") : "";
        }

        // Format date of birth for HTML date input (YYYY-MM-DD)
        let formattedDateOfBirth = "";
        if ("dateofbirth" in editingWriter && editingWriter.dateofbirth != null && editingWriter.dateofbirth !== "") {
          try {
            const dateStr = editingWriter.dateofbirth.toString();
            if (dateStr.includes("T")) {
              formattedDateOfBirth = dateStr.split("T")[0] ?? "";
            }
            else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
              formattedDateOfBirth = dateStr;
            }
          }
          catch (error) {
            console.warn("Error formatting date of birth:", error);
            formattedDateOfBirth = "";
          }
        }

        return {
          firstname,
          lastname,
          middlename,
          caeipinumber: editingWriter.caeipinumber ?? "",
          societycisaccode: ("societycisaccode" in editingWriter) ? (editingWriter.societycisaccode ?? "") : "",
          dateofbirth: formattedDateOfBirth,
          associatedPublisherName: ("publishername" in editingWriter) ? ((editingWriter.publishername as string) ?? "") : "",
          associatedPublisherCAEIPI: ("publishercaeipi" in editingWriter) ? ((editingWriter.publishercaeipi as string) ?? "") : "",
        };
      }

      return {
        firstname: "",
        lastname: "",
        middlename: "",
        caeipinumber: "",
        societycisaccode: "",
        dateofbirth: "",
        associatedPublisherName: "",
        associatedPublisherCAEIPI: "",
      };
    }, [isEditing, editingWriter]),
  });

  // Update form with additional data from API when it loads (but don't clear existing data)
  useEffect(() => {
    if (isEditing && writerQuery.data && editingWriterCode != null && editingWriterCode !== "") {
      const writerData = writerQuery.data;
      const currentValues = form.getValues();

      // Only update fields that aren't already filled or need API-specific data
      const updates: Partial<WriterFormData> = {};

      // Update names only if we get better data from API
      if (writerData.firstname && (!currentValues.firstname || currentValues.firstname !== writerData.firstname)) {
        updates.firstname = writerData.firstname;
      }
      if (writerData.lastname && (!currentValues.lastname || currentValues.lastname !== writerData.lastname)) {
        updates.lastname = writerData.lastname;
      }
      if (writerData.middlename !== undefined && (currentValues.middlename == null || currentValues.middlename !== writerData.middlename)) {
        updates.middlename = writerData.middlename ?? "";
      }

      // Update fields that are only available from API
      if (writerData.societycisaccode != null && writerData.societycisaccode !== "" && (currentValues.societycisaccode == null || currentValues.societycisaccode === "")) {
        updates.societycisaccode = writerData.societycisaccode;
      }

      if (writerData.dateofbirth != null && writerData.dateofbirth !== "" && (currentValues.dateofbirth == null || currentValues.dateofbirth === "")) {
        // Format date of birth for HTML date input
        let formattedDateOfBirth = "";
        try {
          const dateStr = writerData.dateofbirth.toString();
          if (dateStr.includes("T")) {
            formattedDateOfBirth = dateStr.split("T")[0] ?? "";
          }
          else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            formattedDateOfBirth = dateStr;
          }
        }
        catch (error) {
          console.warn("Error formatting date of birth:", error);
        }
        if (formattedDateOfBirth) {
          updates.dateofbirth = formattedDateOfBirth;
        }
      }

      if (writerData.publishername != null && writerData.publishername !== "" && (currentValues.associatedPublisherName == null || currentValues.associatedPublisherName === "")) {
        updates.associatedPublisherName = writerData.publishername as string;
      }

      if (writerData.publishercaeipi != null && writerData.publishercaeipi !== "" && (currentValues.associatedPublisherCAEIPI == null || currentValues.associatedPublisherCAEIPI === "")) {
        updates.associatedPublisherCAEIPI = writerData.publishercaeipi as string;
      }

      // Only update if we have changes to make
      if (Object.keys(updates).length > 0) {
        form.reset({
          ...currentValues,
          ...updates,
        });
      }
    }
  }, [form, isEditing, editingWriterCode, writerQuery.data]);

  // Watch the societycisaccode to determine if it's a US PRO
  const watchedSocietyCode = form.watch("societycisaccode");
  const isUSPro = useMemo(() => {
    return watchedSocietyCode !== undefined && watchedSocietyCode !== null && usPros.includes(watchedSocietyCode);
  }, [watchedSocietyCode, usPros]);

  return {
    form,
    isEditing,
    editingWriter,
    editingWriterFromAvailable,
    writerQuery,
    isUSPro,
    usPros,
  };
}
